import React from 'react';
import { GetStaticProps } from 'next';
import Layout from '../../components/layout';
import NavBar from '../../components/navbar';
import PageHeader from '../../components/zakelijk/PageHeader';
import PageFooterCTA from '../../components/zakelijk/PageFooterCTA';
import StatisticsRow from '../../components/zakelijk/StatisticsRow';
import { getZakelijkPageBySlug, getSponsors } from '../../lib/zakelijk-api';
import { getPrimaryMenuItems } from '../../lib/api';
import { IZakelijkPage } from '../../models/zakelijk-page';
import { IStatistic } from '../../models/statistic';
import { ISponsor } from '../../models/sponsor';
import { IMenuItem } from '../../models/menu-item';
import config from '../../lib/config';
import Link from 'next/link';
import ScalingIframe from '@/components/scaling-iframe';

interface IProps {
	pageData: IZakelijkPage | null;
	sponsors: ISponsor[];
	primaryMenuItems: IMenuItem[];
}

export default function PartnerWordenPage({ pageData, sponsors, primaryMenuItems }: IProps) {
	// Dummy data for development
	const dummyPageData: IZakelijkPage = {
		id: 'dummy-2',
		databaseId: 2,
		title: 'Partner Worden',
		slug: 'partner-worden',
		uri: '/zakelijk/partner-worden',
		acfZakelijkHeader: {
			headerTitle: 'Word Partner',
			headerDescription:
				'<p>Sluit je aan bij onze community van partners en maak impact in de Keuken Kampioen Divisie. Ontdek de mogelijkheden en voordelen van een partnership.</p>',
			contentBadge: 'Partnerships',
		},
		acfZakelijkFooter: {
			footerCtaText: 'Neem contact op',
			footerCtaLink: { uri: '/contact' },
		},
		acfZakelijkStats: {
			stat1Value: '25+',
			stat2Value: '150',
			stat3Value: '500K',
			stat4Value: '200+',
		},
	};

	const displayData = pageData || dummyPageData;
	const pageTitle = `${displayData.acfZakelijkHeader?.headerTitle || displayData.title} | ${config.siteName}`;
	const metaDesc = displayData.acfZakelijkHeader?.headerDescription?.replace(/<[^>]*>/g, '') || displayData.title;

	// Statistics with editable labels from ACF
	const statistics: IStatistic[] = [
		{
			label: displayData.acfZakelijkStats?.stat1Label || 'Jaren Actief',
			value: displayData.acfZakelijkStats?.stat1Value || '—',
		},
		{
			label: displayData.acfZakelijkStats?.stat2Label || 'Partners Bediend',
			value: displayData.acfZakelijkStats?.stat2Value || '—',
		},
		{
			label: displayData.acfZakelijkStats?.stat3Label || 'Impact in Gemeenschap',
			value: displayData.acfZakelijkStats?.stat3Value || '—',
		},
		{
			label: displayData.acfZakelijkStats?.stat4Label || 'Projecten Voltooid',
			value: displayData.acfZakelijkStats?.stat4Value || '—',
		},
	];

	return (
		<Layout preview={false} sponsors={sponsors} metaTitle={pageTitle} metaDesc={metaDesc}>
			<NavBar items={primaryMenuItems} />
			<PageHeader
				title={displayData.acfZakelijkHeader?.headerTitle || displayData.title}
				description={displayData.acfZakelijkHeader?.headerDescription || ''}
				badge={displayData.acfZakelijkHeader?.contentBadge}
			/>

			<StatisticsRow additionalContent={false} statistics={statistics} className="" />

			<section className="bg-white p-8 md:p-16 text-left text-black-500 max-w-2xl mx-auto my-12 rounded-lg">
				<h2 className="text-3xl font-bold mb-4">Neem contact op</h2>
				<p className="text-xl mb-8 text-gray-800">
					Vul het formulier in en wij nemen binnen 2 werkdagen contact met u op.
				</p>

				<ScalingIframe url="https://api.keukenkampioendivisie.nl/zakelijk-formulier/" />
			</section>

			<section className="bg-[#1d2330] p-8 md:p-16 text-center text-white max-w-2xl mx-auto my-12 rounded-lg">
				<p className="text-xl mb-8 opacity-90">
					Download onze uitgebreide brochure met alle partnershipmogelijkheden
				</p>
				<Link
					href={`${config.apiUrl}/wp-content/uploads/2026/07/Brochure-26-27-CED.pdf`}
					className="inline-block bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg text-lg transition-colors"
				>
					Download Partnerbrochure
				</Link>
			</section>

			<PageFooterCTA
				text={displayData.acfZakelijkFooter?.footerCtaText}
				description={displayData.acfZakelijkFooter?.footerCtaDescription}
				link={displayData.acfZakelijkFooter?.footerCtaLink?.uri}
			/>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps<IProps> = async () => {
	try {
		const [pageData, sponsors, primaryMenuItems] = await Promise.all([
			getZakelijkPageBySlug('partner-worden').catch(() => null),
			getSponsors().catch(() => []),
			getPrimaryMenuItems().catch(() => []),
		]);

		return {
			props: {
				pageData,
				sponsors,
				primaryMenuItems,
			},
			revalidate: 3600,
		};
	} catch (error) {
		console.error('Error fetching partner-worden page data:', error);
		return {
			props: {
				pageData: null,
				sponsors: [],
				primaryMenuItems: [],
			},
			revalidate: 3600,
		};
	}
};
